// serializeMarkdown — the pure Element[] → Markdown fold.
//
// The inverse direction of inputRules.ts's markdown detection: where that
// module reads a typed prefix ("# ") OUT of typed content, this module
// writes clean, readable Markdown text back FROM typed content — the
// clipboard's `text/plain` payload for a copy/cut gesture spanning one or
// more blocks (MaterialDocument's onCopy/onCut). Pasting the result into any
// app (plain email, notes, another editor) reads as structured text, never
// raw ElementIds or JSON; pasting a serialized heading BACK into this editor
// re-triggers the SAME `# `-detection inputRules.ts already wires, so
// headings round-trip through the existing live-typing rule for free.
//
// Total over every Element kind (exhaustSwitchCase forces a new variant to
// get its own arm here); no I/O, no DOM — pure text in, text out.

export { elementsToMarkdown, elementToMarkdown, wholeBlockMarkdown };

import { exhaustSwitchCase } from "@bilda/utils";
import type { Choice, Element, McqElement } from "./types.js";

// A-Z, then a numeric fallback past 26 choices (matches renderers.tsx's own
// `letters` list precedent, extended so the fold stays total for any length).
const letterFor = (index: number): string => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letter = letters[index];
    return letter !== undefined ? letter : String(index + 1);
};

// One choice → its Markdown line: "A. text (correct)", with any authored
// feedback as an indented note on the following line. `(correct)` is a
// plain-text marker (not a markdown construct) so the correct answer reads
// unambiguously even where the rest of the line's markup is stripped by a
// dumb renderer.
const choiceToMarkdown = (choice: Choice, index: number): string => {
    const line = `${letterFor(index)}. ${choice.text}${
        choice.correct ? " (correct)" : ""
    }`;
    return choice.feedback ? `${line}\n   ${choice.feedback}` : line;
};

// One MCQ → its stem followed by the lettered choice list, one choice per
// line — reads as a plain question-and-answers block in any plain-text
// context.
const mcqToMarkdown = (element: McqElement): string =>
    [element.stem, ...element.choices.map(choiceToMarkdown)].join("\n");

// One element → its Markdown block. `heading` and `prose` map straight onto
// real Markdown syntax (prose's `markdown` field already IS the source, so
// it passes through unchanged); `objective` and `mcq` have no native
// Markdown construct, so they get a plain, readable rendering instead of an
// invented non-standard syntax.
const elementToMarkdown = (element: Element): string => {
    switch (element.type) {
        case "heading":
            return `${"#".repeat(element.level)} ${element.text}`;
        case "prose":
            return element.markdown;
        case "objective":
            return `Objective: ${element.text}`;
        // The author's own copy: a note reads as a quote.
        case "note":
            return `> ${element.text}`;
        case "mcq":
            return mcqToMarkdown(element);
        // A part is another resource; its text is not this page's.
        case "part":
            return "";
        default:
            return exhaustSwitchCase(element);
    }
};

// A run of elements → one Markdown document, blocks separated by a blank
// line (the standard Markdown block separator, so a heading/paragraph/list
// still parses correctly wherever the text lands). Total: an empty slice
// yields an empty string.
const elementsToMarkdown = (elements: readonly Element[]): string =>
    elements
        .filter((e) => e.type !== "part")
        .map(elementToMarkdown)
        .join("\n\n");

// Collapse runs of whitespace to one space and trim the ends. The rendered
// DOM and the authored Markdown source can disagree on incidental
// whitespace (soft-wrapped lines, a trailing newline) without disagreeing on
// CONTENT — normalizing before comparing keeps `wholeBlockMarkdown` from
// rejecting a genuinely whole-block selection over a cosmetic difference.
const normalizeWhitespace = (text: string): string =>
    text.trim().replace(/\s+/g, " ");

// The single-block copy/cut's clean-Markdown case: a selection that covers
// a block's ENTIRE rendered text, as opposed to a sub-range within it
// (MaterialDocument.tsx's onCopySelection calls this once it already knows
// the selection didn't span multiple blocks). `selectedText` is the
// browser's own stringified Selection; `blockText` is the same block's full
// rendered text pulled from the DOM. If the two match once whitespace is
// normalized, the selection must run from the block's first character to
// its last, so the block's own Markdown SOURCE — not `selectedText`, which
// has already lost any `**`/`_`/`` ` `` markup to the browser's HTML
// rendering — is the correct clipboard payload.
//
// Returns `undefined` for anything short of that: a PARTIAL in-block
// selection (a few words of a paragraph), or the degenerate empty-string
// case. Partial-selection Markdown mapping is a genuinely harder problem —
// mapping a rendered-HTML Range back to a precise offset in the Markdown
// SOURCE, which diverges from the rendered HTML — logged here as a
// follow-up rather than shipped as an approximation that could silently
// truncate or mis-map the copied text.
const wholeBlockMarkdown = (
    element: Element,
    selectedText: string,
    blockText: string,
): string | undefined => {
    const normalized = normalizeWhitespace(selectedText);
    if (normalized === "") return undefined;
    return normalized === normalizeWhitespace(blockText)
        ? elementToMarkdown(element)
        : undefined;
};
